import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  List,
  ListItem,
} from "@mui/material";
import {
  CloudUpload,
  CheckCircle,
  Warning,
  Error,
  Download,
} from "@mui/icons-material";

import Papa from "papaparse";
import * as XLSX from "xlsx";

import {
  UploadProduct,
  ProductUploadResult,
  ProductError,
  CsvProductRow,
  ProductWarning,
} from "types/products";

import ProductPreview from "pages/SelfServe/sharedComponents/ProductPreview";
import { CANONICAL_PRODUCT_HEADERS } from "types/onboarding";
import { DEFAULT_TRADEMARK } from "types/products";
import {
  ASIN_DP_RE,
  ASIN_GP_RE,
  ASIN_RE,
  CONTROL_CHARS_RE,
} from "../utils/catalogUpload";

interface ProductUploadModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: (products: UploadProduct[]) => void;
}

type UploadStepType = "upload" | "validating" | "results";

const MAX_BYTES = 50 * 1024 * 1024;
const MAX_ROWS = 1000;

const normalizeHeader = (h: string) =>
  h.toLowerCase().replace(/[^a-z0-9]/g, "");
const cleanName = (s: string) =>
  (s || "").replace(CONTROL_CHARS_RE, "").replace(/\s+/g, " ").trim();
const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");

const extractASIN = (input: string): string | null => {
  if (!input) return null;
  const raw = input.trim();
  const fromDp = raw.match(ASIN_DP_RE);
  if (fromDp) return fromDp[1].toUpperCase();
  const fromGp = raw.match(ASIN_GP_RE);
  if (fromGp) return fromGp[1].toUpperCase();
  if (ASIN_RE.test(raw)) return raw.toUpperCase();
  return null;
};

const HEADER_MAP: Record<string, keyof UploadProduct> = {
  productname: "name",
  name: "name",
  asin: "amazonId",
  amazonid: "amazonId",
  amazonurl: "amazonId",
  url: "amazonId",
  sku: "sku",
  brand: "brand",
  upc: "upc",
  map: "map",
};

const canonicalToDisplay = (
  canonical: (typeof CANONICAL_PRODUCT_HEADERS)[number]
): string => {
  return canonical === "amazonId" ? "asin" : canonical;
};

const ProductUploadModal: React.FC<ProductUploadModalProps> = ({
  open,
  onClose,
  onAccept,
}) => {
  const [step, setStep] = useState<UploadStepType>("upload");
  const [result, setResult] = useState<ProductUploadResult | null>(null);
  const [validProducts, setValidProducts] = useState<UploadProduct[]>([]);
  const [boundaryError, setBoundaryError] = useState<string | null>(null);

  const handleFileSelect = async (file: File) => {
    setBoundaryError(null);

    if (file.size > MAX_BYTES) {
      setBoundaryError(
        `File is too large (${(file.size / (1024 * 1024)).toFixed(
          1
        )} MB). Max allowed is 50 MB.`
      );
      return;
    }

    setStep("validating");

    try {
      if (file.name.toLowerCase().endsWith(".csv")) {
        const text = await readAsTextUTF8(file);
        const normalized = text.replace(/\r\n?/g, "\n");

        const bad = (normalized.match(/\uFFFD/g) || []).length;
        if (bad > 10) {
          setBoundaryError(
            "CSV does not appear to be UTF-8 encoded. Please export as UTF-8."
          );
          setStep("upload");
          return;
        }

        Papa.parse(normalized, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => {
            const n = normalizeHeader(h);
            return (HEADER_MAP[n] as string) ?? h;
          },
          complete: ({ data, meta }) => {
            if (!meta.fields || meta.fields.length === 0) {
              setBoundaryError(
                "No header row detected. Include a single header row."
              );
              setStep("upload");
              return;
            }
            const validated = validateProducts(data as CsvProductRow[]);
            setResult(validated.valid.length > 0 ? validated : null);
            setValidProducts(validated.valid);
            setStep("results");
          },
          error: () => {
            setBoundaryError(
              "Error parsing CSV file. Ensure it is a valid UTF-8 CSV."
            );
            setStep("upload");
          },
        });
      } else if (file.name.toLowerCase().endsWith(".xlsx")) {
        const ab = await file.arrayBuffer();
        const wb = XLSX.read(ab, { type: "array" });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, {
          defval: "",
        });

        const normalizedRows: CsvProductRow[] = rows.map((row) => {
          const next: Record<string, string> = {};
          Object.entries(row).forEach(([k, v]) => {
            const key = HEADER_MAP[normalizeHeader(k)] ?? k;
            next[key] = v;
          });
          return next;
        });

        const validated = validateProducts(normalizedRows);
        setResult(validated.valid.length > 0 ? validated : null);
        setValidProducts(validated.valid);
        setStep("results");
      } else {
        setBoundaryError(
          "Unsupported file type. Please upload a .csv or .xlsx file."
        );
        setStep("upload");
      }
    } catch (e) {
      console.error(e);
      setBoundaryError("Unexpected error reading file. Please try again.");
      setStep("upload");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFileSelect(file);
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileSelect(file);
  };

  const handleAccept = () => {
    if (validProducts.length > 0) {
      onAccept(validProducts);
      onClose();
    }
  };

  const handleReupload = () => {
    setStep("upload");
    setResult(null);
    setValidProducts([]);
    setBoundaryError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Product Catalog</DialogTitle>
      <DialogContent>
        {step === "upload" && (
          <UploadStep
            onDrop={handleDrop}
            onFileInput={handleFileInput}
            boundaryError={boundaryError}
          />
        )}
        {step === "validating" && <ValidatingStep />}
        {step === "results" && result && (
          <ResultsStep
            validProducts={validProducts}
            setValidProducts={setValidProducts}
            result={result}
            onAccept={handleAccept}
            onReupload={handleReupload}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const UploadStep: React.FC<{
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  boundaryError: string | null;
}> = ({ onDrop, onFileInput, boundaryError }) => (
  <>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Download our template (CSV or Excel), fill it out with your product
      information, and upload it. Already have a catalog on hand? Ensure the
      headers "amazonId" and "name" are included.
    </Typography>
    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
      <Button
        variant="contained"
        onClick={() => downloadTemplate("csv")}
        startIcon={<Download />}
        sx={{
          backgroundColor: "#343a40",
          color: "white",
          "&:hover": { backgroundColor: "#23272b" },
        }}
      >
        Download CSV Template
      </Button>
      <Button
        variant="outlined"
        onClick={() => downloadTemplate("xlsx")}
        startIcon={<Download />}
      >
        Download Excel Template
      </Button>
    </Box>

    {boundaryError && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {boundaryError}
      </Alert>
    )}

    <Box
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      sx={{
        border: "2px dashed #ccc",
        borderRadius: 2,
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        "&:hover": { borderColor: "#666" },
      }}
    >
      <input
        type="file"
        accept=".csv,.xlsx"
        onChange={onFileInput}
        style={{ display: "none" }}
        id="file-upload"
      />
      <label htmlFor="file-upload">
        <CloudUpload sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
        <Typography variant="h6">
          Drop .csv or .xlsx here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Max 50 MB, up to 1,000 rows, one header row
        </Typography>
      </label>
    </Box>
  </>
);

const ValidatingStep = () => (
  <Box sx={{ textAlign: "center", py: 4 }}>
    <Typography variant="h6" sx={{ mb: 2 }}>
      Validating your products...
    </Typography>
    <LinearProgress />
  </Box>
);

const ResultsStep: React.FC<{
  result: ProductUploadResult;
  validProducts: UploadProduct[];
  setValidProducts: React.Dispatch<React.SetStateAction<UploadProduct[]>>;
  onAccept: () => void;
  onReupload: () => void;
  onCancel: () => void;
}> = ({
  result,
  onAccept,
  onReupload,
  onCancel,
  validProducts,
  setValidProducts,
}) => {
  const hasErrors = result.errors.length > 0;

  const handleUpdate = (index: number, updatedProduct: UploadProduct) => {
    setValidProducts((prev) =>
      prev.map((p, idx) => (idx === index ? updatedProduct : p))
    );
  };
  const handleDelete = (index: number) => {
    setValidProducts((prev) => prev.filter((_, idx) => idx !== index));
  };

  const preview = useMemo(() => validProducts.slice(0, 10), [validProducts]);

  const handleAcceptClick = () => {
    onAccept();
  };

  return (
    <>
      <Alert
        severity={hasErrors ? "warning" : "success"}
        sx={{ mb: 2 }}
        icon={hasErrors ? <Warning /> : <CheckCircle />}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Upload Results
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<CheckCircle />}
            label={`${result.valid.length} valid products`}
            color="success"
            size="small"
            sx={{ mr: 1 }}
          />
          {result.duplicatesRemoved > 0 && (
            <Chip
              icon={<Warning />}
              label={`${result.duplicatesRemoved} duplicates removed`}
              color="warning"
              size="small"
              sx={{ mr: 1 }}
            />
          )}
          {result.errors.length > 0 && (
            <Chip
              icon={<Error />}
              label={`${result.errors.length} errors`}
              color="error"
              size="small"
            />
          )}
        </Box>
      </Alert>

      {result.errors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Errors (rows skipped):
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 200,
              overflow: "auto",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            {result.errors.slice(0, 10).map((error, idx) => (
              <ListItem key={idx}>
                <Typography variant="body2" color="error">
                  Row {error.row}: {error.message}
                </Typography>
              </ListItem>
            ))}
            {result.errors.length > 10 && (
              <ListItem>
                <Typography variant="body2" color="text.secondary">
                  ... and {result.errors.length - 10} more errors
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      )}

      {preview.length > 0 && (
        <ProductPreview
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          products={preview}
        />
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onReupload}>Re-upload</Button>
        <Button
          variant="contained"
          onClick={handleAcceptClick}
          disabled={validProducts.length === 0}
        >
          Accept {result.valid.length} Products
        </Button>
      </Box>
    </>
  );
};

const readAsTextUTF8 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(String(fr.result || ""));
    fr.readAsText(file, "utf-8");
  });

const validateProducts = (rows: CsvProductRow[]): ProductUploadResult => {
  const valid: UploadProduct[] = [];
  const errors: ProductError[] = [];
  const warnings: ProductWarning[] = [];
  const seenAsins = new Set<string>();
  let duplicatesRemoved = 0;

  if (rows.length > MAX_ROWS) {
    return {
      valid: [],
      warnings: [],
      errors: [
        {
          row: 0,
          data: {},
          message: `File exceeds ${MAX_ROWS.toLocaleString()} row limit (found ${rows.length.toLocaleString()} rows)`,
        },
      ],
      duplicatesRemoved: 0,
    };
  }

  const canonicalHeaders = new Set(
    CANONICAL_PRODUCT_HEADERS.map(canonicalToDisplay)
  );

  const looksLikeHeaderRow = (r: CsvProductRow) => {
    const vals = Object.values(r).map((v) => String(v).toLowerCase());
    let hits = 0;
    for (const v of vals) if (canonicalHeaders.has(v)) hits++;
    return hits >= 2;
  };

  let extraHeaderWarningAdded = false;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 2;

    if (looksLikeHeaderRow(row)) {
      if (!extraHeaderWarningAdded) {
        warnings.push({
          product: row.name || row.asin || "unknown",
          row: rowNum,
          message: "Extra header row detected and skipped.",
        });
        extraHeaderWarningAdded = true;
      }
      continue;
    }

    const name = cleanName(str(row.name));
    const amazonIdInput = str(row.amazonId);
    const sku = str(row.sku);
    const brand = str(row.brand);
    const upc = str(row.upc);
    const map = str(row.map);

    if (!name) {
      errors.push({
        row: rowNum,
        data: row,
        message: "Missing required field: name",
      });
      continue;
    }

    const amazonId = extractASIN(amazonIdInput);
    if (!amazonId) {
      errors.push({
        row: rowNum,
        data: row,
        message: "Invalid ASIN or Amazon URL",
      });
      continue;
    }

    if (seenAsins.has(amazonId)) {
      duplicatesRemoved++;
      continue;
    }
    seenAsins.add(amazonId);

    valid.push({
      name,
      amazonId,
      sku,
      brand,
      upc,
      map,
      trademark: DEFAULT_TRADEMARK,
    });
  }

  return { valid, warnings, errors, duplicatesRemoved };
};

const downloadTemplate = (type: "csv" | "xlsx") => {
  const headers = CANONICAL_PRODUCT_HEADERS.filter(
    (header) => header !== "trademark"
  ).map(canonicalToDisplay);

  if (type === "csv") {
    const csv = headers.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sigil-product-template.csv";
    a.click();
  } else {
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "sigil-product-template.xlsx", { compression: true });
  }
};

export default ProductUploadModal;
